// src/admin/PrintDroppedOutStudents.js - Sắp xếp danh sách thiếu nhi nghỉ luôn theo thứ tự Cấp Lớp
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './PrintDroppedOutStudents.css';

const PrintDroppedOutStudents = () => {
  const { currentYearId: contextYearId } = useContext(StudentContext);
  const outletContext = useOutletContext() || {};
  const selectedClass = outletContext.selectedClassForOutlet || 'all';

  const [currentYearId, setCurrentYearId] = useState('');
  const [currentYearName, setCurrentYearName] = useState('');
  const [students, setStudents] = useState([]);
  const [classesMap, setClassesMap] = useState({});
  const [loading, setLoading] = useState(true);

  // Bộ lọc tìm kiếm
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Xác định Niên khóa hiện tại
        let yearId = contextYearId;
        let yearName = '';

        if (!yearId) {
          const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
          const yearSnap = await getDocs(yearQuery);
          if (!yearSnap.empty) {
            yearId = yearSnap.docs[0].id;
            yearName = yearSnap.docs[0].data().name || yearId;
          } else {
            const allYearsSnap = await getDocs(query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc')));
            if (!allYearsSnap.empty) {
              yearId = allYearsSnap.docs[0].id;
              yearName = allYearsSnap.docs[0].data().name || yearId;
            }
          }
        }

        if (!yearId) {
          setLoading(false);
          return;
        }

        setCurrentYearId(yearId);
        setCurrentYearName(yearName);

        // 2. Tải Cấp Lớp (grades), Danh mục Lớp (classes), Phân Lớp (enrollments) và Hồ Sơ (students)
        const [gradesSnap, classesSnap, enrollmentsSnap, studentsSnap] = await Promise.all([
          getDocs(collection(firestore, `academic_years/${yearId}/grades`)),
          getDocs(collection(firestore, `academic_years/${yearId}/classes`)),
          getDocs(collection(firestore, `academic_years/${yearId}/enrollments`)),
          getDocs(collection(firestore, 'students'))
        ]);

        // Map thứ tự sắp xếp của Cấp Lớp (gradeOrderMap)
        const gradeOrderMap = {};
        gradesSnap.docs.forEach((d) => {
          const gData = d.data();
          gradeOrderMap[d.id] = gData.sortOrder ?? 999;
        });

        // Map metadata của Lớp (Tên, thứ tự cấp lớp, thứ tự lớp)
        const cMap = {};
        const classMetaMap = {};
        classesSnap.docs.forEach((d) => {
          const cData = d.data();
          cMap[d.id] = cData.name || d.id;
          classMetaMap[d.id] = {
            name: cData.name || d.id,
            gradeOrder: gradeOrderMap[cData.gradeId] ?? 999,
            classSortOrder: cData.sortOrder ?? 999
          };
        });
        setClassesMap(cMap);

        const enrollmentsMap = {};
        enrollmentsSnap.docs.forEach((d) => {
          const data = d.data();
          enrollmentsMap[data.studentId || d.id] = data;
        });

        // 3. Lọc danh sách thiếu nhi Nghỉ luôn
        const dropoutList = [];

        studentsSnap.docs.forEach((docSnap) => {
          const sId = docSnap.id;
          const profile = docSnap.data();
          const enr = enrollmentsMap[sId];

          const statusRaw = String(profile.status || enr?.status || '').trim();
          const isDropout =
            statusRaw === 'Nghỉ luôn' ||
            statusRaw === 'Nghỉ học' ||
            statusRaw === 'Đã nghỉ' ||
            statusRaw.toLowerCase().includes('nghỉ');

          if (isDropout) {
            const assignedClassId = enr?.classId || profile.classNameId || profile.classId || 'CHUA_XEP_LOP';
            const assignedClassName = cMap[assignedClassId] || profile.className || assignedClassId || 'Chưa xếp lớp';
            const classMeta = classMetaMap[assignedClassId] || { gradeOrder: 999, classSortOrder: 999 };

            dropoutList.push({
              id: sId,
              holyName: profile.holyName || '',
              fullName: profile.fullName || sId,
              gender: profile.gender || 'nam',
              classId: assignedClassId,
              className: assignedClassName,
              gradeOrder: classMeta.gradeOrder,
              classSortOrder: classMeta.classSortOrder
            });
          }
        });

        // 🌟 4. SẮP XẾP CHUẨN XÁC: Cấp Lớp (sortOrder) ➔ Thứ Tự Lớp ➔ Tên Lớp ➔ Tên Thiếu Nhi
        const getLastName = (fullName) => {
          if (!fullName) return '';
          const parts = fullName.trim().split(/\s+/);
          return parts[parts.length - 1].toLowerCase();
        };

        dropoutList.sort((a, b) => {
          // Ưu tiên 1: Thứ tự Cấp Lớp (gradeOrder)
          if ((a.gradeOrder ?? 999) !== (b.gradeOrder ?? 999)) {
            return (a.gradeOrder ?? 999) - (b.gradeOrder ?? 999);
          }
          // Ưu tiên 2: Thứ tự Lớp học trong Cấp Lớp (classSortOrder)
          if ((a.classSortOrder ?? 999) !== (b.classSortOrder ?? 999)) {
            return (a.classSortOrder ?? 999) - (b.classSortOrder ?? 999);
          }
          // Ưu tiên 3: Tên Lớp
          if (a.className !== b.className) {
            return a.className.localeCompare(b.className, 'vi');
          }
          // Ưu tiên 4: Tên Thiếu Nhi (từ cuối cùng trong Họ và Tên)
          const lastNameA = getLastName(a.fullName);
          const lastNameB = getLastName(b.fullName);
          const cmp = lastNameA.localeCompare(lastNameB, 'vi');
          if (cmp !== 0) return cmp;
          return (a.fullName || '').localeCompare(b.fullName || '', 'vi');
        });

        setStudents(dropoutList);
      } catch (err) {
        console.error('Lỗi khi tải danh sách thiếu nhi nghỉ luôn:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [contextYearId]);

  // Lọc theo Sidebar chọn lớp và Tìm kiếm
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      if (selectedClass && selectedClass !== 'all' && s.classId !== selectedClass) {
        return false;
      }

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        const holyName = s.holyName.toLowerCase();
        const fullName = s.fullName.toLowerCase();
        const id = s.id.toLowerCase();
        const className = s.className.toLowerCase();

        return (
          id.includes(term) ||
          holyName.includes(term) ||
          fullName.includes(term) ||
          className.includes(term)
        );
      }

      return true;
    });
  }, [students, selectedClass, searchTerm]);

  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = () => {
    if (filteredStudents.length === 0) {
      alert('Không có dữ liệu để xuất Excel.');
      return;
    }

    const header = ['STT', 'Mã TN', 'Tên Thánh', 'Họ và Tên', 'Lớp'];

    const data = filteredStudents.map((s, idx) => [
      idx + 1,
      s.id,
      s.holyName || '',
      s.fullName,
      s.className
    ]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    XLSX.utils.book_append_sheet(wb, ws, 'DS_NghiLuon');

    const classNameSlug =
      selectedClass === 'all' || !classesMap[selectedClass]
        ? 'TatCaLop'
        : classesMap[selectedClass].replace(/\s+/g, '_');

    XLSX.writeFile(wb, `DS_ThieuNhi_NghiLuon_${classNameSlug}_${currentYearId || 'NienKhoa'}.xlsx`);
  };

  const currentClassName = classesMap[selectedClass] || selectedClass;

  return (
    <div className="pds-container">
      {/* THANH ĐIỀU KHIỂN & BỘ LỌC (ẨN KHI IN) */}
      <div className="pds-toolbar no-print">
        <div className="pds-toolbar-left">
          <div className="pds-search-box">
            <input
              type="text"
              placeholder="🔎 Tìm theo Mã TN, Tên Thánh, Họ và Tên..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pds-search-input"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="pds-btn-clear" title="Xóa tìm kiếm">
                ✕
              </button>
            )}
          </div>
        </div>

        <div className="pds-toolbar-right">
          <button onClick={handleExportExcel} className="pds-btn pds-btn-excel">
            📥 Xuất Excel ({filteredStudents.length})
          </button>
          <button onClick={handlePrint} className="pds-btn pds-btn-print">
            🖨️ In Danh Sách (A4)
          </button>
        </div>
      </div>

      {/* KHUNG TRÌNH BÀY IN ẤN (CHUẨN FORM A4) */}
      <div className="pds-sheet">
        {/* PHẦN ĐẦU (GÓC TRÁI GIÁO XỨ & XỨ ĐOÀN) */}
        <div className="pds-header-top">
          <div className="pds-origin-left">
            <div className="pds-parish-name">GIÁO XỨ AN NHƠN</div>
            <div className="pds-group-name">XỨ ĐOÀN PHAOLÔ LÊ VĂN LỘC</div>
            <div className="pds-header-divider"></div>
          </div>
        </div>

        {/* PHẦN TIÊU ĐỀ DẠNG BANNER */}
        <section className="pds-title-section">
          <div className="pds-title-banner">
            <h1 className="pds-main-title">DANH SÁCH THIẾU NHI NGHỈ LUÔN</h1>
            <h2 className="pds-class-title">
              {selectedClass === 'all' ? 'TẤT CẢ CÁC LỚP' : `LỚP: ${currentClassName.toUpperCase()}`}
            </h2>
          </div>
          <p className="pds-year-text">Niên khóa: {currentYearName || 'Niên Khóa 2026 - 2027'}</p>
        </section>

        {/* BẢNG DANH SÁCH (ĐÃ SẮP XẾP THEO CẤP LỚP) */}
        <section className="pds-table-section">
          <table className="pds-table">
            <thead>
              <tr>
                <th className="pds-col-stt">STT</th>
                <th className="pds-col-id">Mã TN</th>
                <th className="pds-col-holy">Tên Thánh</th>
                <th className="pds-col-name">Họ và Tên</th>
                <th className="pds-col-class">Lớp</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="pds-text-center" style={{ padding: '24px', color: '#64748b' }}>
                    ⏳ Đang tải danh sách thiếu nhi nghỉ luôn...
                  </td>
                </tr>
              ) : filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="5" className="pds-text-center" style={{ padding: '24px', color: '#64748b' }}>
                    Không có thiếu nhi nào nghỉ luôn trong lớp/bộ lọc đang chọn.
                  </td>
                </tr>
              ) : (
                filteredStudents.map((s, idx) => (
                  <tr key={s.id || idx}>
                    <td className="pds-text-center">{idx + 1}</td>
                    <td className="pds-col-id pds-text-center">{s.id}</td>
                    <td>{s.holyName || '-'}</td>
                    <td className="pds-font-bold">{s.fullName}</td>
                    <td className="pds-text-center">{s.className}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

export default PrintDroppedOutStudents;